import React, { useState, useEffect } from 'react';
// Update imports for React Router DOM v6
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation
} from 'react-router-dom';
import './App.css';
import data241 from './data/241.json';
import data242 from './data/242.json';
import data351 from './data/351.json';
import data361 from './data/361.json';
import { generateQuestions } from './utils/gameUtils';
import { saveGameResult } from './firebase/gameService';
import { AuthProvider, useAuth } from './firebase/AuthContext';

// Components
import BottomNav from './components/navigation/BottomNav';
import SubjectSelection from './components/SubjectSelection';
import GamePlay from './components/GamePlay';
import ResultDisplay from './components/ResultDisplay';
import Login from './components/auth/Login';
import UserProfile from './components/profile/UserProfile';
import Leaderboard from './components/leaderboard/Leaderboard';
import PageTransition from './components/common/PageTransition';

/**
 * Enhanced Layout Component with PageTransition
 * 
 * Provides the common layout structure for all routes with smooth page transitions
 * Uses simplified, consistent iOS-style animations
 * 
 * @returns {JSX.Element} The layout component with animated transitions
 */
const Layout = () => {
  // Get current location to detect navigation patterns
  const location = useLocation();
  
  // Reset scroll position on page change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="app">
      <main className="app-main" tabIndex="-1">
        <PageTransition>
          <Outlet />
        </PageTransition>
      </main>
      <BottomNav />
    </div>
  );
};

/**
 * Protected Route Component
 * 
 * Wrapper for routes that require authentication
 * Redirects to login if user is not authenticated
 * 
 * @param {Object} props - Component props
 * @returns {JSX.Element} Protected route or redirect
 */
const ProtectedRoute = ({ children }) => {
  const { currentUser } = useAuth();
  
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

/**
 * Game Component
 * 
 * Main game logic and state management
 * Acts as the container for the game flow
 * 
 * @returns {JSX.Element} Game component
 */
const Game = () => {
  // Core game state management
  const [gameStarted, setGameStarted] = useState(false);
  const [subject, setSubject] = useState(null);
  const [currentWork, setCurrentWork] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [askedQuestions, setAskedQuestions] = useState(new Set());
  const [inputValue, setInputValue] = useState('');
  const [gameResult, setGameResult] = useState(null);
  const [gameOver, setGameOver] = useState(false);
  const [debugInfo, setDebugInfo] = useState({});
  const [completedWorks, setCompletedWorks] = useState(new Set());
  const [allWorksCompleted, setAllWorksCompleted] = useState(false);
  const [currentDataset, setCurrentDataset] = useState([]);
  const [failureReason, setFailureReason] = useState(null);
  
  // Use a ref to prevent double-start during initial render
  const isInitialRender = React.useRef(true);
  
  // Debug state to track selector triggers
  const [selectorTriggers, setSelectorTriggers] = useState(0);
  
  // User authentication
  const { currentUser } = useAuth();

  /**
   * One-time initialization effect to ensure proper initial state
   * Prevents accidental game start on initial render
   */
  React.useEffect(() => {
    console.log("Game component initial render");
    // Reset the game on first render to ensure we start from a clean state
    if (isInitialRender.current) {
      isInitialRender.current = false;
      setGameStarted(false);
    }
  }, []);

  /**
   * Effect to check if all works have been completed whenever completedWorks changes
   */
  useEffect(() => {
    if (completedWorks.size === 15 && subject) {
      setAllWorksCompleted(true);
      setGameOver(true);
    }
  }, [completedWorks, subject]);

  /**
   * Selects a random work from the dataset that hasn't been completed yet
   * 
   * @param {Array} dataset - The dataset to select from
   * @returns {Object|null} The selected work or null if all works are completed
   */
  const selectRandomUncompletedWork = React.useCallback((dataset) => {
    // Safety check for invalid dataset
    if (!dataset || !Array.isArray(dataset) || dataset.length === 0) {
      console.error('Invalid dataset provided to selectRandomUncompletedWork');
      return null;
    }
    
    // Filter out works that have already been completed
    const uncompletedWorks = dataset.filter(work => !completedWorks.has(work.work));
    
    // If all works have been completed, return null
    if (uncompletedWorks.length === 0) {
      return null;
    }
    
    // Randomly select a work from the uncompleted works
    const randomIndex = Math.floor(Math.random() * uncompletedWorks.length);
    return uncompletedWorks[randomIndex];
  }, [completedWorks]);

  /**
   * Handles the selection of subject (241 or 242)
   * Initializes the game with a random work from the chosen dataset
   * Contains comprehensive validation to prevent edge cases
   * 
   * @param {string} selectedSubject - The selected subject code
   */
  const handleSubjectSelect = (selectedSubject) => {
    // Debug logging for selection lifecycle
    console.log("🎮 GAME: handleSubjectSelect called with:", selectedSubject);
    
    // Track selector triggers for debugging state issues
    setSelectorTriggers(prev => prev + 1);
    
    // Validate subject selection
    if (!selectedSubject || (selectedSubject !== '241' && selectedSubject !== '242' && selectedSubject !== '351' && selectedSubject !== '361')) {
      console.error('Invalid subject selected:', selectedSubject);
      return;
    }
    
    // CRITICAL FIX: First set game state to false to ensure clean start
    setGameStarted(false);
    
    // Set subject and get the appropriate dataset
    console.log(`🎮 GAME: Setting subject to ${selectedSubject}`);
    setSubject(selectedSubject);
    
    // Get data based on subject
    let data;
    if (selectedSubject === '241') {
      data = data241;
    } else if (selectedSubject === '242') {
      data = data242;
    } else if (selectedSubject === '351') {
      data = data351;
    } else if (selectedSubject === '361') {
      data = data361;
    }
    
    // Debug dataset
    console.log(`🎮 GAME: Dataset for ${selectedSubject} loaded with ${data ? data.length : 0} items`);
    
    // Validate dataset
    if (!data || !Array.isArray(data) || data.length === 0) {
      console.error('Invalid dataset for subject:', selectedSubject);
      setFailureReason('Error loading game data. Please try again.');
      return;
    }
    
    // Update dataset state
    setCurrentDataset(data);
    
    // Reset completed works when switching subjects
    setCompletedWorks(new Set());
    setAllWorksCompleted(false);
    
    // Select a random work that hasn't been completed yet
    const randomWork = selectRandomUncompletedWork(data);
    
    // Safety check for randomWork
    if (!randomWork) {
      console.error('Failed to select a random work for subject:', selectedSubject);
      setFailureReason('Error selecting a work. Please try again.');
      return;
    }
    
    console.log("🎮 GAME: Selected random work:", randomWork.work);
    
    // Update current work state
    setCurrentWork(randomWork);
    
    try {
      // Generate questions and initialize game state
      const { questions: shuffledQuestions, initialAskedQuestions } = generateQuestions(randomWork);
      console.log(`🎮 GAME: Generated ${shuffledQuestions.length} questions`);
      
      // Ensure we're properly tracking the first question
      if (shuffledQuestions.length > 0) {
        const firstQuestionType = shuffledQuestions[0].type;
        initialAskedQuestions.add(firstQuestionType);
        console.log(`🎮 GAME: Marked first question type as asked: ${firstQuestionType}`);
      }
      
      // Update game state in sequence
      setQuestions(shuffledQuestions);
      setAskedQuestions(initialAskedQuestions);
      setCurrentQuestionIndex(0);
      setUserAnswers({});
      setDebugInfo({});
      setFailureReason(null);
      
      // CRITICAL FIX: Use separate statement with small timeout to ensure state updates are processed
      console.log("🎮 GAME: Starting game in 50ms...");
      setTimeout(() => {
        console.log("🎮 GAME: Starting game NOW!");
        setGameStarted(true);
      }, 50);
      
    } catch (error) {
      console.error('Error generating questions:', error);
      setFailureReason('Error starting the game. Please try again.');
    }
  };

  // Debugging effect to track game state changes
  useEffect(() => {
    console.log(`🎮 GAME STATE: gameStarted=${gameStarted}, subject=${subject}, selectorTriggers=${selectorTriggers}`);
  }, [gameStarted, subject, selectorTriggers]);

  /**
   * Restarts the game with a new random work from the current dataset
   * Reset all game state but maintain the selected subject
   */
  const restartGame = () => {
    console.log("🎮 GAME: Restarting game with user:", currentUser ? "Logged in" : "Not logged in");
    
    // Reset game state
    setGameOver(false);
    setGameResult(null);
    setUserAnswers({});
    setAskedQuestions(new Set());
    setInputValue('');
    setFailureReason(null);
    
    try {
      // Select a new random work that hasn't been completed yet
      const randomWork = selectRandomUncompletedWork(currentDataset);
      
      // If all works have been completed, show completion screen
      if (!randomWork) {
        setAllWorksCompleted(true);
        setGameOver(true);
        return;
      }
      
      setCurrentWork(randomWork);
      
      // Generate questions for the new work
      const { questions: shuffledQuestions, initialAskedQuestions } = generateQuestions(randomWork);
      setQuestions(shuffledQuestions);
      setAskedQuestions(initialAskedQuestions);
      setCurrentQuestionIndex(0);
      
      // Start the game immediately to avoid delay issues
      setGameStarted(true);
    } catch (error) {
      console.error('Error restarting game:', error);
      setFailureReason('Error restarting the game. Please try again.');
      goToSubjectSelection();
    }
  };

  /**
   * Marks a work as completed and saves the result to the database
   * Updates the game state and user records with the completion
   * 
   * @param {string} work - The work that was completed
   * @param {boolean} isCompleted - Whether the work was successfully completed
   * @param {number} correctAnswers - Number of correctly answered questions
   * @param {number} totalQuestionsAsked - Total number of questions asked
   * @returns {Promise<void>}
   */
  const markWorkAsCompleted = async (work, isCompleted, correctAnswers, totalQuestionsAsked) => {
    console.log("Marking work as completed:", {
      work,
      isCompleted,
      correctAnswers,
      totalQuestionsAsked,
      currentUser: currentUser ? "Logged in" : "Not logged in"
    });
    
    // Add the work to the completed works set
    const newCompletedWorks = new Set(completedWorks);
    if (isCompleted) {
      newCompletedWorks.add(work);
    }
    setCompletedWorks(newCompletedWorks);
    
    // Save the result to the database if the user is authenticated
    if (currentUser) {
      try {
        // Save game result with the new points system
        await saveGameResult({
          userId: currentUser.uid,
          subject,
          work,
          isCompleted,
          questionsAsked: totalQuestionsAsked, // Total questions that were asked
          correctAnswers: correctAnswers, // Number of correctly answered questions
          userAnswers: {}  // Provide empty object since it's required by the function signature
        });
      } catch (error) {
        console.error('Error saving game result:', error);
        // Don't block the game flow on save errors
      }
    } else {
      // For non-logged in users, just log and proceed without database saving
      console.log('User not logged in, game result not saved to database');
    }
    
    // Check if all works have been completed
    if (newCompletedWorks.size === 15) {
      setAllWorksCompleted(true);
    }
  };
  
  /**
   * Returns to the subject selection screen
   * Reset all game state
   */
  const goToSubjectSelection = () => {
    console.log("🎮 GAME: Returning to subject selection");
    
    // Reset all game state
    setGameStarted(false);
    setSubject(null);
    setCurrentWork(null);
    setQuestions([]);
    setCurrentQuestionIndex(0);
    setUserAnswers({});
    setAskedQuestions(new Set());
    setInputValue('');
    setGameResult(null);
    setGameOver(false);
    setDebugInfo({});
    setFailureReason(null);
    setCompletedWorks(new Set());
    setAllWorksCompleted(false);
    setCurrentDataset([]);
  };

  /**
   * Sets a reason for game failure to display to the user
   * 
   * @param {string} reason - The reason for failure
   */
  const setGameFailureReason = (reason) => {
    setFailureReason(reason);
  };

  // Memoize onSubjectSelect to prevent unnecessary rerenders
  const memoizedOnSubjectSelect = React.useCallback(handleSubjectSelect, [selectRandomUncompletedWork]);

  // ADDITIONAL DEBUG to track when component renders with critical props
  useEffect(() => {
    console.log("Game component props updated", {
      gameStarted,
      subject,
      onSubjectSelectDefined: typeof memoizedOnSubjectSelect === 'function'
    });
  }, [gameStarted, subject, memoizedOnSubjectSelect]);

  return (
    <div className="game-container">
      {!gameStarted ? (
        <div className="ios-fade-in">
          <SubjectSelection 
            onSubjectSelect={memoizedOnSubjectSelect} 
            key="subject-selection"
          />
        </div>
      ) : !gameOver ? (
        <div className="ios-fade-in">
          <GamePlay 
            currentWork={currentWork}
            questions={questions}
            currentQuestionIndex={currentQuestionIndex}
            setCurrentQuestionIndex={setCurrentQuestionIndex}
            userAnswers={userAnswers}
            setUserAnswers={setUserAnswers}
            askedQuestions={askedQuestions}
            setAskedQuestions={setAskedQuestions}
            inputValue={inputValue}
            setInputValue={setInputValue}
            setGameResult={setGameResult}
            setGameOver={setGameOver}
            setDebugInfo={setDebugInfo}
            subject={subject}
            markWorkAsCompleted={markWorkAsCompleted}
            setGameFailureReason={setGameFailureReason}
            currentUser={currentUser}
          />
        </div>
      ) : (
        <div className="ios-fade-in">
          <ResultDisplay 
            gameResult={gameResult}
            questions={questions}
            currentWork={currentWork}
            debugInfo={debugInfo}
            askedQuestions={askedQuestions}
            restartGame={restartGame}
            goToSubjectSelection={goToSubjectSelection}
            completedWorks={completedWorks}
            allWorksCompleted={allWorksCompleted}
            failureReason={failureReason}
          />
        </div>
      )}
    </div>
  );
};

/**
 * App Component
 * 
 * Main application entry point with enhanced navigation and transitions
 * 
 * @returns {JSX.Element} The application with routes and transitions
 */
function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route element={<Layout />}>
            <Route path="/" element={<Game />} />
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute>
                  <UserProfile />
                </ProtectedRoute>
              } 
            />
            <Route path="/login" element={<Login />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
          </Route>
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
